//
// # SchematicPickerDialog
//
// Modal dialog for selecting which schematic to navigate to when a symbol
// links to multiple schematics via schematicImpls.
// Supports pdkId filtering when entries are available.
//

import React, { useState, useCallback, useMemo } from "react";
import { Ident } from "GenAlphaModel";
import type { schema } from "GenAlphaSchema";
import { Modal } from "../Modal/Modal";
import type { PickerEntry } from "../../types";

const ALL_PDKS = "__all__";
const NO_PDK = "__none__";

interface SchematicPickerDialogProps {
  readonly schematics: ReadonlyArray<schema.symbol.SchematicImplRef>;
  readonly entries: ReadonlyArray<PickerEntry> | null;
  readonly sourcePdkId?: string;
  readonly onSelect: (ident: schema.Ident) => void;
  readonly onCancel: () => void;
}

export function SchematicPickerDialog({
  schematics,
  entries,
  sourcePdkId,
  onSelect,
  onCancel,
}: SchematicPickerDialogProps) {
  const [pdkFilter, setPdkFilter] = useState<string>(sourcePdkId ?? ALL_PDKS);

  // Collect unique pdkIds from entries (only real pdkId strings, no sentinels)
  const { pdkIds, hasNoPdk } = useMemo(() => {
    if (!entries) return { pdkIds: [] as string[], hasNoPdk: false };
    const pdks = new Set<string>();
    let noPdk = false;
    for (const entry of entries) {
      if (entry.pdkId) pdks.add(entry.pdkId);
      else noPdk = true;
    }
    return { pdkIds: Array.from(pdks).sort(), hasNoPdk: noPdk };
  }, [entries]);

  // If entries are loaded, filter them; otherwise fall back to raw schematics
  const displayItems: ReadonlyArray<{
    ident: schema.Ident;
    displayName: string;
    pdkId?: string;
  }> = useMemo(() => {
    if (!entries) {
      // Still loading pdkIds — show raw list unfiltered
      return schematics.map((ref) => ({
        ident: ref.target,
        displayName: Ident.displayName(ref.target),
      }));
    }
    if (pdkFilter === ALL_PDKS) return entries;
    if (pdkFilter === NO_PDK) return entries.filter((e) => !e.pdkId);
    return entries.filter((e) => e.pdkId === pdkFilter || !e.pdkId);
  }, [entries, schematics, pdkFilter]);

  const [selectedIndex, setSelectedIndex] = useState(0);

  const confirmSelection = useCallback(() => {
    const item = displayItems[selectedIndex];
    if (item) onSelect(item.ident);
  }, [displayItems, selectedIndex, onSelect]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        confirmSelection();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, displayItems.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      }
    },
    [displayItems, confirmSelection]
  );

  const showFilter = pdkIds.length > 1 || (pdkIds.length > 0 && hasNoPdk);

  return (
    <Modal open={true} onClose={onCancel} title="Go to Schematic">
      <div onKeyDown={handleKeyDown}>
        {/* PDK filter */}
        {showFilter && (
          <div className="flex items-center gap-2 mb-3">
            <span
              className="text-[10px] font-semibold uppercase tracking-widest"
              style={{ color: "var(--color-ide-text-muted)" }}
            >
              PDK
            </span>
            <select
              value={pdkFilter}
              onChange={(e) => {
                setPdkFilter(e.target.value);
                setSelectedIndex(0);
              }}
              className="text-[11px] rounded border px-1.5 py-0.5 outline-none"
              style={{
                backgroundColor: "var(--color-ide-bg)",
                borderColor: "var(--color-ide-border)",
                color: "var(--color-ide-text)",
              }}
            >
              <option value={ALL_PDKS}>All</option>
              {pdkIds.map((pdk) => (
                <option key={pdk} value={pdk}>
                  {pdk}
                </option>
              ))}
              {hasNoPdk && <option value={NO_PDK}>None (PDK-agnostic)</option>}
            </select>
          </div>
        )}

        {/* Schematic list */}
        <div className="flex flex-col gap-1">
          {displayItems.map((item, idx) => (
            <label
              key={Ident.stringify(item.ident)}
              className="flex items-center gap-2.5 px-2 py-1.5 rounded cursor-pointer"
              style={{
                backgroundColor:
                  selectedIndex === idx
                    ? "var(--color-ide-surface-2)"
                    : "transparent",
              }}
              onClick={() => setSelectedIndex(idx)}
            >
              <input
                type="radio"
                name="schematic-selection"
                checked={selectedIndex === idx}
                onChange={() => setSelectedIndex(idx)}
                style={{ accentColor: "var(--color-ide-accent)" }}
              />
              <span className="text-[13px] text-[var(--color-ide-text)]">
                {item.displayName}
              </span>
              {item.pdkId && (
                <span
                  className="text-[9px] px-1 py-0.5 rounded"
                  style={{
                    backgroundColor: "var(--color-ide-surface-2)",
                    color: "var(--color-ide-text-dim)",
                  }}
                >
                  PDK: {item.pdkId}
                </span>
              )}
            </label>
          ))}
          {displayItems.length === 0 && (
            <div
              className="text-xs px-2 py-1.5 italic"
              style={{ color: "var(--color-ide-text-dim)" }}
            >
              {entries ? "None matching filter" : "Loading..."}
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 text-[13px] rounded border cursor-pointer bg-transparent"
            style={{
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={confirmSelection}
            disabled={displayItems.length === 0}
            className="px-4 py-1.5 text-[13px] rounded border-none cursor-pointer"
            style={{
              backgroundColor:
                displayItems.length > 0
                  ? "var(--color-ide-accent)"
                  : "var(--color-ide-bg)",
              color:
                displayItems.length > 0
                  ? "#ffffff"
                  : "var(--color-ide-text-dim)",
            }}
          >
            Open
          </button>
        </div>
      </div>
    </Modal>
  );
}
